/**
 * Utility functions for tracking client source (marketing attribution)
 * Captures and stores the source of visitors (TikTok, Google, Facebook, etc.)
 * Also tracks campaign tags from Facebook campaign names
 */

const CLIENT_SOURCE_KEY = 'brain_event_client_source';
const CAMPAIGN_TAG_KEY = 'brain_event_campaign_tag';
const UTM_CAMPAIGN_KEY = 'brain_event_utm_campaign';

/**
 * Get client source from URL parameters or localStorage
 * Priority: URL params > localStorage > null
 */
export function getClientSource() {
  // Check URL parameters first
  const urlParams = new URLSearchParams(window.location.search);
  
  // Check for custom 'from' parameter (e.g., from=email-marketing)
  const fromParam = urlParams.get('from');
  if (fromParam) {
    const normalizedSource = normalizeSource(fromParam);
    if (normalizedSource) {
      saveClientSource(normalizedSource);
      return normalizedSource;
    }
  }
  
  // Check for UTM source parameter
  const utmSource = urlParams.get('utm_source');
  if (utmSource) {
    const normalizedSource = normalizeSource(utmSource);
    if (normalizedSource) {
      saveClientSource(normalizedSource);
      return normalizedSource;
    }
  }
  
  // Check for custom 'source' parameter
  const sourceParam = urlParams.get('source');
  if (sourceParam) {
    const normalizedSource = normalizeSource(sourceParam);
    if (normalizedSource) {
      saveClientSource(normalizedSource);
      return normalizedSource;
    }
  }
  
  // Check for TikTok-specific parameter
  const ttclid = urlParams.get('ttclid');
  if (ttclid) {
    saveClientSource('tiktok');
    return 'tiktok';
  }
  
  // Check for Google-specific parameter
  const gclid = urlParams.get('gclid');
  if (gclid) {
    saveClientSource('google');
    return 'google';
  }
  
  // Check for Facebook-specific parameter
  const fbclid = urlParams.get('fbclid');
  if (fbclid) {
    saveClientSource('facebook');
    return 'facebook';
  }
  
  // Fall back to stored value in localStorage
  return getStoredClientSource();
}

/**
 * Normalize source string to standard format
 * Handles both full names and abbreviations (e.g., 'ig' -> 'instagram')
 */
function normalizeSource(source) {
  const normalized = source.toLowerCase().trim();
  
  // Map common variations to standard names
  const sourceMap = {
    // TikTok
    'tiktok': 'tiktok',
    'tik tok': 'tiktok',
    'tik-tok': 'tiktok',
    'tt': 'tiktok',
    
    // Google
    'google': 'google',
    'google ads': 'google',
    'googleads': 'google',
    'adwords': 'google',
    'gads': 'google',
    
    // Facebook
    'facebook': 'facebook',
    'fb': 'facebook',
    'meta': 'facebook',
    'meta ads': 'facebook',
    
    // Instagram
    'instagram': 'instagram',
    'ig': 'instagram',
    'insta': 'instagram',
    
    // LinkedIn
    'linkedin': 'linkedin',
    'li': 'linkedin',
    
    // YouTube
    'youtube': 'youtube',
    'yt': 'youtube',
    
    // Twitter/X
    'twitter': 'twitter',
    'x': 'twitter',
    
    // Organic/Direct
    'organic': 'organic',
    'direct': 'organic',
    'none': 'organic',
    '(direct)': 'organic',
    
    // Email
    'email': 'email',
    'newsletter': 'email',
    'mail': 'email',
    'email-marketing': 'email-marketing',
    'email_marketing': 'email-marketing',
    'emailmarketing': 'email-marketing',
    
    // Other platforms
    'snapchat': 'snapchat',
    'snap': 'snapchat',
    'pinterest': 'pinterest',
    'pin': 'pinterest',
    'whatsapp': 'whatsapp',
    'wa': 'whatsapp',
  };
  
  const mappedSource = sourceMap[normalized];
  
  if (mappedSource) {
        return mappedSource;
  }
  
  // If not in map, return original normalized value
    return normalized;
}

/**
 * Save client source to localStorage
 */
function saveClientSource(source) {
  try {
    localStorage.setItem(CLIENT_SOURCE_KEY, source);
      } catch (error) {
      }
}

/**
 * Get stored client source from localStorage
 */
function getStoredClientSource() {
  try {
    return localStorage.getItem(CLIENT_SOURCE_KEY);
  } catch (error) {
        return null;
  }
}

/**
 * Clear stored client source (useful for testing)
 */
export function clearClientSource() {
  try {
    localStorage.removeItem(CLIENT_SOURCE_KEY);
    localStorage.removeItem(CAMPAIGN_TAG_KEY);
    localStorage.removeItem(UTM_CAMPAIGN_KEY);
      } catch (error) {
      }
}

/**
 * Get UTM campaign ID from URL or localStorage
 */
export function getUtmCampaign() {
  // Check URL parameters first
  const urlParams = new URLSearchParams(window.location.search);
  const utmCampaign = urlParams.get('utm_campaign');
  
  if (utmCampaign) {
    saveUtmCampaign(utmCampaign);
    return utmCampaign;
  }
  
  // Fall back to stored value
  return getStoredUtmCampaign();
}

/**
 * Save UTM campaign to localStorage
 */
function saveUtmCampaign(campaign) {
  try {
    localStorage.setItem(UTM_CAMPAIGN_KEY, campaign);
      } catch (error) {
      }
}

/**
 * Get stored UTM campaign from localStorage
 */
function getStoredUtmCampaign() {
  try {
    return localStorage.getItem(UTM_CAMPAIGN_KEY);
  } catch (error) {
        return null;
  }
}

/**
 * Get campaign tag (extracted from Facebook campaign name)
 */
export function getCampaignTag() {
  try {
    return localStorage.getItem(CAMPAIGN_TAG_KEY);
  } catch (error) {
        return null;
  }
}

/**
 * Save campaign tag to localStorage
 */
export function saveCampaignTag(tag) {
  try {
    localStorage.setItem(CAMPAIGN_TAG_KEY, tag);
      } catch (error) {
      }
}

/**
 * Initialize campaign tracking on page load
 * Note: Campaign tag extraction is now handled by the backend for security
 */
export function initCampaignTracking() {
  const utmCampaign = getUtmCampaign();
  
  if (utmCampaign) {
          }
}

/**
 * Initialize client source tracking on page load
 * Call this early in your app initialization
 */
export function initClientSourceTracking() {
  const source = getClientSource();
  if (source) {
      } else {
    // ✅ Don't automatically set as "organic" - let it remain null
    // This allows us to distinguish between true organic traffic and unknown sources
      }
}
